import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useAuth} from "../../auth";
import {ErrorBanner, Modal} from "../../components";
import {navigate, useRoute} from "../../hooks";
import {adminSignUp, archiveMerchant, listMerchants} from "../../api";
import type {MerchantResponse} from "../../api";
import {
    generateMerchantPortalPassword,
    normalizeMerchantPortalLogin,
    validateMerchantPortalCredentials,
} from "../../utils";
import {MerchantDetails} from "./MerchantDetails";

const MERCHANT_LIST_LIMIT = 200;
const MERCHANTS_ROUTE = "#/admin/merchants";

// Детали мерчанта открываются под-роутом #/admin/merchants/{id}: так работают
// переходы из popover/баннера уведомлений. Сам список остаётся на #/admin/merchants.
function parseMerchantIdFromRoute(route: string): string | null {
    if (!route.startsWith(`${MERCHANTS_ROUTE}/`)) return null;
    const rest = route.slice(MERCHANTS_ROUTE.length + 1).split(/[/?]/)[0].trim();
    return rest || null;
}

function shortId(id: string): string {
    if (id.length <= 12) return id;
    return `${id.slice(0, 8)}…${id.slice(-4)}`;
}

function matchesMerchant(merchant: MerchantResponse, query: string): boolean {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (
        merchant.name.toLowerCase().includes(q) ||
        merchant.id.toLowerCase().includes(q)
    );
}

function formatMerchantBalance(merchant: MerchantResponse): string {
    const amount = merchant.amount || "0";
    return merchant.currency ? `${amount} ${merchant.currency}` : amount;
}

export function AdminMerchantsPage(): JSX.Element {
    const {token} = useAuth();
    const route = useRoute();
    const selectedId = useMemo(() => parseMerchantIdFromRoute(route), [route]);

    const [merchants, setMerchants] = useState<MerchantResponse[]>([]);
    const [query, setQuery] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<unknown>(null);
    const [success, setSuccess] = useState("");

    const openMerchant = useCallback((id: string): void => {
        navigate(`${MERCHANTS_ROUTE}/${id}`);
    }, []);
    const backToList = useCallback((): void => {
        navigate(MERCHANTS_ROUTE);
    }, []);

    const [createOpen, setCreateOpen] = useState(false);
    const [merchantName, setMerchantName] = useState("");
    const [merchantLogin, setMerchantLogin] = useState("");
    const [merchantPassword, setMerchantPassword] = useState("");
    const [merchantPasswordConfirmation, setMerchantPasswordConfirmation] = useState("");
    const [merchantPasswordVisible, setMerchantPasswordVisible] = useState(false);
    const [merchantIsVip, setMerchantIsVip] = useState(false);
    const [createSaving, setCreateSaving] = useState(false);
    const [createError, setCreateError] = useState<unknown>(null);

    const [archiveTarget, setArchiveTarget] = useState<MerchantResponse | null>(null);
    const [archiveSaving, setArchiveSaving] = useState(false);
    const [archiveError, setArchiveError] = useState<unknown>(null);

    const reload = useCallback((): void => {
        if (!token) return;
        setLoading(true);
        setError(null);
        listMerchants(token, {limit: MERCHANT_LIST_LIMIT})
            .then((resp) => setMerchants(resp.items))
            .catch((err) => setError(err))
            .finally(() => setLoading(false));
    }, [token]);

    useEffect(() => {
        reload();
    }, [reload]);

    const filtered = useMemo(
        () => merchants.filter((merchant) => matchesMerchant(merchant, query)),
        [merchants, query],
    );

    const clearCreateForm = (): void => {
        setCreateError(null);
        setMerchantName("");
        setMerchantLogin("");
        setMerchantPassword("");
        setMerchantPasswordConfirmation("");
        setMerchantPasswordVisible(false);
        setMerchantIsVip(false);
    };

    const openCreate = (): void => {
        clearCreateForm();
        setCreateOpen(true);
    };

    const closeCreate = (): void => {
        if (createSaving) return;
        setCreateOpen(false);
        clearCreateForm();
    };

    const submitCreate = (e: React.FormEvent): void => {
        e.preventDefault();
        if (!token) {
            setCreateError("Требуется повторный вход.");
            return;
        }
        const name = merchantName.trim();
        if (!name) {
            setCreateError("Укажите имя мерчанта.");
            return;
        }
        const credentialProblem = validateMerchantPortalCredentials(
            merchantLogin,
            merchantPassword,
            merchantPasswordConfirmation,
        );
        if (credentialProblem) {
            setCreateError(credentialProblem);
            return;
        }
        const login = normalizeMerchantPortalLogin(merchantLogin);

        setCreateSaving(true);
        setCreateError(null);
        adminSignUp(token, {
            login,
            password: merchantPassword,
            name,
            type: "merchant",
            roles: ["merchant"],
            isActive: true,
            isVip: merchantIsVip,
        })
            .then(async ({referenceId}) => {
                clearCreateForm();
                setCreateOpen(false);
                setSuccess(`Мерчант создан: ${shortId(referenceId)}.`);
                await listMerchants(token, {limit: MERCHANT_LIST_LIMIT}).then((resp) => {
                    setMerchants(resp.items);
                });
            })
            .catch((err) => setCreateError(err))
            .finally(() => setCreateSaving(false));
    };

    const confirmArchive = (): void => {
        if (!token || !archiveTarget) return;
        const target = archiveTarget;
        setArchiveSaving(true);
        setArchiveError(null);
        archiveMerchant(token, target.id)
            .then(() => {
                setMerchants((prev) => prev.filter((m) => m.id !== target.id));
                setSuccess(`Мерчант "${target.name}" архивирован.`);
                setArchiveTarget(null);
                if (selectedId === target.id) {
                    backToList();
                }
            })
            .catch((err) => setArchiveError(err))
            .finally(() => setArchiveSaving(false));
    };

    if (selectedId) {
        return (
            <MerchantDetails
                merchantId={selectedId}
                onBack={() => {
                    backToList();
                    reload();
                }}
                onArchived={(id) => {
                    setMerchants((prev) => prev.filter((m) => m.id !== id));
                    backToList();
                    setSuccess("Мерчант архивирован.");
                }}
            />
        );
    }

    return (
        <div className="page admin-merchants">
            <div className="page-header">
                <div>
                    <h2>Merchants</h2>
                    <p className="page-subtitle">{merchants.length} active merchants</p>
                </div>
                <div className="page-actions">
                    <button className="btn btn-ghost btn-sm" type="button" onClick={reload} disabled={loading}>
                        {loading ? "Обновление…" : "Обновить"}
                    </button>
                    <button className="btn btn-primary btn-sm" type="button" onClick={openCreate}>
                        Создать мерчанта
                    </button>
                </div>
            </div>

            {success && (
                <div className="alert alert-info">
                    {success}
                    <button className="alert-dismiss" type="button" onClick={() => setSuccess("")}>
                        ×
                    </button>
                </div>
            )}
            <ErrorBanner error={error} title="Список мерчантов не загружен" />

            <div className="admin-toolbar">
                <input
                    type="search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Фильтр по имени или ID"
                    autoComplete="off"
                />
            </div>

            <section className="merchant-results">
                {loading ? (
                    <div className="empty-state">Загрузка мерчантов…</div>
                ) : (
                    <div className="table-wrapper">
                        <table>
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Name</th>
                                    <th>Balance</th>
                                    <th>Max payments / order</th>
                                    {/* Не «бронь»: с 00062 это число задаёт срок
                                        ЖИЗНИ заявки, а не время удержания взятой.
                                        Отсчёт идёт от создания. */}
                                    <th>Срок заявки</th>
                                    <th>VIP</th>
                                    <th>Кабинет площадки</th>
                                    <th>Заявки</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.length === 0 && (
                                    <tr>
                                        <td colSpan={9} className="empty-state">
                                            Мерчанты не найдены.
                                        </td>
                                    </tr>
                                )}
                                {filtered.map((merchant) => (
                                    <tr
                                        key={merchant.id}
                                        className="merchant-row"
                                        onClick={() => openMerchant(merchant.id)}
                                    >
                                        <td className="mono merchant-id-cell">{merchant.id}</td>
                                        <td>
                                            <strong>{merchant.name}</strong>
                                        </td>
                                        <td className="mono">{formatMerchantBalance(merchant)}</td>
                                        <td>{merchant.maxPaymentsPerOrder}</td>
                                        <td>{merchant.claimHoldMinutes} min</td>
                                        <td>
                                            <span className={`merchant-vip-value ${merchant.isVip ? "active" : ""}`}>
                                                {merchant.isVip ? "VIP" : "Нет"}
                                            </span>
                                        </td>
                                        <td className="mono">
                                            {merchant.external
                                                ? `${merchant.external.source} · ${merchant.external.accountCode}${
                                                      merchant.external.autoProvisioned ? " (авто)" : ""
                                                  }`
                                                : "—"}
                                        </td>
                                        <td>
                                            {merchant.external ? (
                                                <span
                                                    className={`merchant-vip-value ${
                                                        merchant.external.ordersEnabled ? "active" : ""
                                                    }`}
                                                >
                                                    {merchant.external.ordersEnabled ? "Принимаем" : "Выключены"}
                                                </span>
                                            ) : (
                                                "—"
                                            )}
                                        </td>
                                        <td className="merchant-action-cell">
                                            <button
                                                className="btn btn-ghost btn-sm"
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    openMerchant(merchant.id);
                                                }}
                                            >
                                                Открыть
                                            </button>
                                            <button
                                                className="btn btn-danger btn-sm"
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setArchiveError(null);
                                                    setArchiveTarget(merchant);
                                                }}
                                            >
                                                Удалить
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            <Modal
                open={createOpen}
                title="Создать мерчанта"
                onClose={closeCreate}
                sizeClassName="modal-sm"
            >
                <form className="onboarding-form" onSubmit={submitCreate}>
                    <ErrorBanner error={createError} title="Мерчант не создан" />
                    <div className="form-group">
                        <label htmlFor="merchant-name">Имя</label>
                        <input
                            id="merchant-name"
                            type="text"
                            value={merchantName}
                            onChange={(e) => setMerchantName(e.target.value)}
                            autoComplete="off"
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="merchant-login">Логин личного кабинета</label>
                        <input
                            id="merchant-login"
                            type="text"
                            value={merchantLogin}
                            onChange={(e) => setMerchantLogin(e.target.value)}
                            autoComplete="off"
                            spellCheck={false}
                        />
                        <small>3–100 символов: латиница, цифры, точка, дефис или подчёркивание.</small>
                    </div>
                    <div className="form-group">
                        <label htmlFor="merchant-password">Пароль личного кабинета</label>
                        <input
                            id="merchant-password"
                            type={merchantPasswordVisible ? "text" : "password"}
                            value={merchantPassword}
                            onChange={(e) => setMerchantPassword(e.target.value)}
                            autoComplete="new-password"
                        />
                        <button
                            className="btn btn-ghost btn-sm"
                            type="button"
                            onClick={() => {
                                const generated = generateMerchantPortalPassword();
                                setMerchantPassword(generated);
                                setMerchantPasswordConfirmation(generated);
                                setMerchantPasswordVisible(true);
                            }}
                        >
                            Сгенерировать безопасный пароль
                        </button>
                        <button
                            className="btn btn-ghost btn-sm"
                            type="button"
                            aria-controls="merchant-password merchant-password-confirmation"
                            aria-pressed={merchantPasswordVisible}
                            onClick={() => setMerchantPasswordVisible((visible) => !visible)}
                        >
                            {merchantPasswordVisible ? "Скрыть пароль" : "Показать пароль"}
                        </button>
                        <small>Не менее 15 символов; максимум 72 байта.</small>
                    </div>
                    <div className="form-group">
                        <label htmlFor="merchant-password-confirmation">Повторите пароль</label>
                        <input
                            id="merchant-password-confirmation"
                            type={merchantPasswordVisible ? "text" : "password"}
                            value={merchantPasswordConfirmation}
                            onChange={(e) => setMerchantPasswordConfirmation(e.target.value)}
                            autoComplete="new-password"
                        />
                    </div>
                    <label className="toggle-row" htmlFor="merchant-is-vip">
                        <input
                            id="merchant-is-vip"
                            type="checkbox"
                            checked={merchantIsVip}
                            onChange={(e) => setMerchantIsVip(e.target.checked)}
                        />
                        <span>
                            <strong>VIP-мерчант</strong>
                            <small>Заявки поднимаются выше в пуле трейдера.</small>
                        </span>
                    </label>
                    <div className="modal-actions">
                        <button
                            className="btn btn-ghost"
                            type="button"
                            onClick={closeCreate}
                            disabled={createSaving}
                        >
                            Отмена
                        </button>
                        <button className="btn btn-primary" type="submit" disabled={createSaving}>
                            {createSaving ? "Создание…" : "Создать"}
                        </button>
                    </div>
                </form>
            </Modal>

            <Modal
                open={archiveTarget !== null}
                title="Удалить мерчанта"
                onClose={() => setArchiveTarget(null)}
                sizeClassName="modal-sm"
            >
                <ErrorBanner error={archiveError} title="Мерчант не архивирован" />
                <p className="modal-hint">
                    Это архивирование: финансовая история сохраняется, активный API-токен
                    отзывается, доступ в личный кабинет отключается, все browser-сессии
                    завершаются, мерчант пропадает из списка.
                </p>
                {archiveTarget && (
                    <div className="token-merchant-id mono">{archiveTarget.name} · {archiveTarget.id}</div>
                )}
                <div className="modal-actions">
                    <button
                        className="btn btn-ghost"
                        type="button"
                        onClick={() => setArchiveTarget(null)}
                        disabled={archiveSaving}
                    >
                        Отмена
                    </button>
                    <button className="btn btn-danger" type="button" onClick={confirmArchive} disabled={archiveSaving}>
                        {archiveSaving ? "Архивирование…" : "Архивировать"}
                    </button>
                </div>
            </Modal>
        </div>
    );
}
